import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { STATUS_COLOR } from '../constants/statusColors';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardMedia, Typography, Box, IconButton, Tooltip, useTheme, useMediaQuery } from '@mui/material';
import type { PopoverProps } from '@mui/material';
import { Anime } from '../types/Anime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditNoteIcon from '@mui/icons-material/EditNote';
import Button from '@mui/material/Button';
import { setAnimeStatus, EMPTY_ANIME_REVIEW } from '../services/animeApi';
import CardStatusMenu, { CardStatusItem } from './common/CardStatusMenu';
import { isRightClickMenuEnabled } from '../utils/gameCardPrefs';
import ConfirmDialog from './dialogs/ConfirmDialog';
import AnimeReviewDialog from './AnimeReviewDialog';
import { cardHoverSx, CARD_EDGE_CLASS, CARD_EDGE_SX, CARD_ZOOM_CLASS, CARD_SHEEN_CLASS, CARD_SHEEN_SX } from '../constants/cardHover';
import { useCardScoreIconColor } from './profile/accentScope';

interface AnimeCardProps {
  anime: Anime;
  initialStatus?: number | null;
  mode?: 'search' | 'library' | 'public';
  initialScore?: number | null;
  initialStartDate?: string | null;
  initialSubStatus?: number[] | null;
  initialScoreTags?: number[] | null;
  initialEpisodesWatched?: number | null;
  initialWatchMedium?: number[] | null;
}

const AnimeCard: React.FC<AnimeCardProps> = ({
  anime,
  initialStatus,
  mode = 'search',
  initialScore,
  initialStartDate,
  initialSubStatus,
  initialScoreTags,
  initialEpisodesWatched,
  initialWatchMedium,
}) => {
  const navigate = useNavigate();
  // アニメ・映画で語彙が同じなので名前空間を共有している
  const { t } = useTranslation('mediaCard');
  const [status, setStatus] = useState<number | null>(initialStatus ?? null);
  const theme = useTheme();
  // 右クリックはPCのみ。モバイルは長押しがブラウザ標準の選択操作と競合する
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const scoreIconColor = useCardScoreIconColor();
  const [anchorEl, setAnchorEl] = useState<PopoverProps['anchorEl']>(null);
  const [isStatusChanging, setIsStatusChanging] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [openReviewDialog, setOpenReviewDialog] = useState(false);
  // 編集で開いたか。編集なら initialData を渡さず、ダイアログに取得させる
  const [reviewDialogIsEdit, setReviewDialogIsEdit] = useState(false);
  const [isLoggedIn] = useState(() => !!localStorage.getItem('token'));
  const [score, setScore] = useState<number | null>(initialScore ?? null);
  const [startDate, setStartDate] = useState<string | null>(initialStartDate ?? null);
  const [subStatus, setSubStatus] = useState<number[] | null>(initialSubStatus ?? null);
  const watchMedium = initialWatchMedium ?? null;

  React.useEffect(() => {
    setStatus(initialStatus ?? null);
    setScore(initialScore ?? null);
    setStartDate(initialStartDate ?? null);
    setSubStatus(initialSubStatus ?? null);
  }, [initialStatus, initialScore, initialStartDate, initialSubStatus]);

  const displayTitle = anime.title_japanese || anime.title_english || anime.title;

  // 放送開始前かどうか（日付ベースで判定）
  const isUpcoming = React.useMemo(() => {
    if (!anime.aired_from) return false;
    const airedDate = new Date(anime.aired_from);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return airedDate > today;
  }, [anime.aired_from]);
  const isWishlisted = status === 5;

  const handleWishlistClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isLoggedIn) { navigate('/login'); return; }
    try {
      const newStatus = isWishlisted ? null : 5;
      await setAnimeStatus(anime.mal_id, newStatus);
      setStatus(newStatus);
    } catch (error) {
      console.error('ウォッチリストの更新に失敗しました', error);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 8.5) return '#4CAF50';
    if (score >= 7.5) return '#8BC34A';
    if (score >= 6.5) return '#CDDC39';
    if (score >= 5.5) return '#FFC107';
    if (score >= 4.5) return '#FF9800';
    return '#F44336';
  };

  const getStatusIcon = (statusCode: number | null) => {
    switch (statusCode) {
      case 1: return <CheckCircleOutlineIcon fontSize="small" sx={{ color: STATUS_COLOR[1] }} />;
      case 2: return <PlayCircleOutlineIcon fontSize="small" sx={{ color: 'info.main' }} />;
      case 3: return <PauseCircleOutlineIcon fontSize="small" sx={{ color: 'warning.main' }} />;
      case 4: return <BlockIcon fontSize="small" sx={{ color: '#b99a71' }} />;
      case 5: return <StarBorderIcon fontSize="small" sx={{ color: 'secondary.main' }} />;
      default: return <AddCircleOutlineIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
    }
  };

  // ツールチップ・読み上げ用の完全な表記。セグメントの短い表記は `status.N` 側
  const getStatusText = () => (
    status === null ? t('status.none') : t(`statusFull.${status}`)
  );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (mode === 'public') return;
    setAnchorEl(event.currentTarget);
    setIsStatusChanging(true);
  };

// PC: カードを右クリックしてもステータスメニューを開けるようにする。
  // **GameCard と同じ規則にすること。** 設定(gameCardPrefs)も共有していて、
  // メディアごとに挙動が違うと「ゲームでは効くのにアニメでは効かない」になる。
  // 公開モード・モバイル（長押しを尊重）・設定OFF のときは何もしない。
  const handleContextMenu = (event: React.MouseEvent<HTMLElement>) => {
    if (mode === 'public' || !isMdUp || !isRightClickMenuEnabled()) return;
    event.preventDefault();
    event.stopPropagation();
    // コンテキストメニューの慣習どおりカーソル位置に開く（仮想アンカー要素）
    const x = event.clientX;
    const y = event.clientY;
    setAnchorEl({
      nodeType: 1,
      getBoundingClientRect: () => new DOMRect(x, y, 0, 0),
    } as PopoverProps['anchorEl']);
    setIsStatusChanging(true);
  };

  /**
   * メニューから直接レビュー編集を開く。
   * **ここでデータを取りに行かないこと。** initialData を渡さなければ
   * ダイアログが自分で取得する（GameCard と同じ）。カード側で詰め替えると、
   * 項目が増えたときに漏れて既存レビューが空で保存される。
   */
  const handleOpenReviewEdit = () => {
    setAnchorEl(null);
    setIsStatusChanging(false);
    setReviewDialogIsEdit(true);
    setOpenReviewDialog(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setIsStatusChanging(false);
  };

  const handleStatusChange = async (newStatus: number | null) => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    if (newStatus === null) {
      setOpenDeleteConfirm(true);
      return;
    }
    try {
      await setAnimeStatus(anime.mal_id, newStatus);
      const prevStatus = status;
      setStatus(newStatus);

      // 初回のステータス設定時（null→1/2/3）のみレビューダイアログを表示（検索モードのみ）
      if (mode === 'search' && prevStatus === null && [1, 2, 3].includes(newStatus)) {
        // ここは新規ステータス。編集モードでは開かない
        setReviewDialogIsEdit(false);
        setOpenReviewDialog(true);
      }
    } catch (error) {
      console.error('ステータスの更新に失敗しました', error);
    }
    handleClose();
  };

  const handleDeleteConfirm = async () => {
    try {
      await setAnimeStatus(anime.mal_id, null);
      setStatus(null);
    } catch (error) {
      console.error('ステータスの削除に失敗しました', error);
    }
    setOpenDeleteConfirm(false);
    handleClose();
  };

  const renderScore = () => {
    if (!anime.score) return null;
    const animeScore = Number(anime.score);
    if (isNaN(animeScore)) return null;
    const scoreColor = getScoreColor(animeScore);

    return (
      <Box
        sx={{
          position: 'absolute',
          bottom: { xs: 2, sm: 4 },
          right: { xs: 2, sm: 4 },
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)',
          borderRadius: '6px',
          padding: '2px 6px',
          display: 'flex',
          alignItems: 'center',
          border: `1px solid ${scoreColor}33`,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: scoreColor,
            fontWeight: 'bold',
            fontSize: '0.7rem',
            lineHeight: 1,
          }}
        >
          {animeScore.toFixed(2)}
        </Typography>
      </Box>
    );
  };

  const renderUserScore = () => {
    if (score === null || score === undefined || (mode !== 'library' && mode !== 'public')) return null;

    return (
      <Box
        sx={{
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          borderRadius: '6px',
          padding: '2px 6px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <StarIcon sx={{ color: scoreIconColor, fontSize: '0.9rem', mr: 0.5 }} />
        <Typography
          variant="caption"
          sx={{
            color: 'white',
            fontWeight: 'bold',
            lineHeight: 1.2,
            display: 'flex',
            alignItems: 'center'
          }}
        >
          {typeof score === 'number' ? score.toFixed(1) : score}
        </Typography>
      </Box>
    );
  };

  const renderRewatchIcon = () => {
    if ((mode !== 'library' && mode !== 'public') || !subStatus || !subStatus.includes(1)) return null;

    return (
      <Box
        sx={{
          background: 'linear-gradient(45deg, #f0f0f0d8, #bebebed2)',
          borderRadius: '6px',
          padding: '2px 4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 0.5,
        }}
      >
        <Tooltip title="再視聴" arrow>
          <RefreshIcon sx={{ color: '#383838', fontSize: { xs: '0.9rem', sm: '1rem' } }} />
        </Tooltip>
      </Box>
    );
  };

  const WATCH_MEDIUM_LABELS: Record<number, string> = {
    1: '劇場',
    2: '再上映',
    3: 'TV',
    4: 'VOD',
    5: 'パッケージ',
    6: '試写会',
  };

  const renderWatchMediumInfo = () => {
    if ((mode !== 'library' && mode !== 'public') || !watchMedium || watchMedium.length === 0) return null;

    const text = watchMedium.map(id => WATCH_MEDIUM_LABELS[id]).filter(Boolean).join(', ');
    if (!text) return null;

    return (
      <Box
        sx={{
          position: 'absolute',
          maxWidth: { xs: '45%', sm: '70%' },
          bottom: 4,
          left: 4,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          borderRadius: '4px',
          padding: '0px 4px',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          backdropFilter: 'blur(4px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: 'white',
            fontSize: { xs: '0.5rem', sm: '0.6rem' },
            fontWeight: 'medium',
            lineHeight: 1.2,
            opacity: 0.9,
          }}
        >
          {text}
        </Typography>
      </Box>
    );
  };

  const renderPlayInfo = () => {
    if (mode !== 'library' && mode !== 'public') return null;

    if (startDate) {
      const d = new Date(startDate);
      const formattedDate = `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}`;

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          <PlayCircleOutlineIcon sx={{ mr: 0.25, color: 'text.secondary', flexShrink: 0, fontSize: { xs: '0.85rem', sm: '1rem' } }} />
          <Typography variant="caption" sx={{ lineHeight: 1, color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {formattedDate}
          </Typography>
        </Box>
      );
    }

    return (
      <Typography variant="caption" color="text.secondary">
        {displayYear || '不明'}
      </Typography>
    );
  };

  const renderTypeBadge = () => {
    if (!anime.type || anime.type === 'TV') return null;
    return (
      <Box
        sx={{
          position: 'absolute',
          top: { xs: 2, sm: 4 },
          right: { xs: 2, sm: 4 },
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
          borderRadius: '3px',
          padding: '2px 4px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: 'white', fontSize: '0.55rem', fontWeight: 400, lineHeight: 1, m: 0, p: 0 }}
        >
          {anime.type}
        </Typography>
      </Box>
    );
  };

  const getDisplayYear = () => {
    if (anime.year) return anime.year;
    if (anime.aired_from) return new Date(anime.aired_from).getFullYear();
    return null;
  };

  const displayYear = getDisplayYear();

  // 色は既存の指定を踏襲（1 だけ statusColors.ts、2/3/5 はテーマトークン、4 は生 hex の混在）。
  // statusColors.ts へ寄せるのは別作業なので、ここでは見た目を変えない。
  const statusItems: CardStatusItem[] = [
    { code: 1, icon: <CheckCircleOutlineIcon />, color: () => STATUS_COLOR[1], label: t('status.1'), fullLabel: t('statusFull.1') },
    { code: 2, icon: <PlayCircleOutlineIcon />, color: (th) => th.palette.info.main, label: t('status.2'), fullLabel: t('statusFull.2') },
    { code: 3, icon: <PauseCircleOutlineIcon />, color: (th) => th.palette.warning.main, label: t('status.3'), fullLabel: t('statusFull.3') },
    { code: 4, icon: <BlockIcon />, color: () => '#b99a71', label: t('status.4'), fullLabel: t('statusFull.4') },
    { code: 5, icon: <StarBorderIcon />, color: (th) => th.palette.secondary.main, label: t('status.5'), fullLabel: t('statusFull.5') },
  ];

  // ステータスがある自分のカードだけ。ここから直接レビュー編集を開ける
  const menuActions = status !== null && mode !== 'public'
    ? [{ icon: <EditNoteIcon />, label: t('menu.editStatus'), onClick: handleOpenReviewEdit }]
    : [];

  return (
    <>
      <Box
        component={Link}
        to={`/anime/${anime.mal_id}`}
        sx={{ textDecoration: 'none', display: 'block', height: '100%' }}
        onContextMenu={handleContextMenu}
        onClick={(e) => {
          if (isStatusChanging) { e.preventDefault(); return; }
        }}
      >
        <Card
          elevation={0}
          sx={(theme) => ({
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            // 地と同色のベタ塗りにしない。カードを背景へ溶かしたいだけなら透明で足りる。
            // 実際に敷いていると、地が単色でなくなった瞬間（プロフィールの環境光など）に
            // カード下部だけ #121212 の四角として浮き上がる。
            backgroundColor: 'transparent',
            // ゲームカードの既定に揃える（角）。こちらは設定を持たないので固定
            borderRadius: '0px',
            overflow: 'hidden',
            position: 'relative',
            ...cardHoverSx(theme),
          })}
        >
          <CardMedia
            component="div"
            sx={{
              height: 0,
              paddingTop: '133.33%',
              borderRadius: '0px',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* 画像は独立した層に置く。CardMedia ごと拡大すると
                上に載っているバッジやステータスボタンまで一緒に拡大されてしまう。 */}
            <Box
              className={CARD_ZOOM_CLASS}
              sx={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `url("${anime.image_url || '/image/no_image.png'}")`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
            <Box className={CARD_SHEEN_CLASS} sx={CARD_SHEEN_SX} />
            <Box className={CARD_EDGE_CLASS} sx={CARD_EDGE_SX} />
            {renderTypeBadge()}
            <Box
              sx={{
                position: 'absolute',
                top: 4,
                left: 4,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {renderRewatchIcon()}
            </Box>
            <Box
              sx={{
                position: 'absolute',
                bottom: 4,
                right: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end'
              }}
            >
              {renderUserScore()}
            </Box>
            {renderWatchMediumInfo()}
          </CardMedia>
          <CardContent sx={{ flexGrow: 1, p: 0, pt: 1, '&:last-child': { pb: 1 } }}>
            <Typography
              variant="subtitle2"
              component="div"
              noWrap
              sx={{ fontSize: { xs: '0.65rem', sm: '0.725rem' }, fontWeight: '600', mb: 0 }}
            >
              {displayTitle}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', minWidth: 0 }}>
              {(mode === 'library' || mode === 'public') ? (
                renderPlayInfo()
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {displayYear || '不明'}
                </Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 0.5, flexShrink: 0 }}>
                {mode === 'search' && anime.episodes && !isUpcoming && anime.type !== 'Movie' && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    {anime.episodes}話
                  </Typography>
                )}
                {isUpcoming ? (
                  <Button
                    size="small"
                    onClick={handleWishlistClick}
                    sx={{
                      height: { xs: '20px', sm: '22px' },
                      minWidth: { xs: '60px', sm: '68px' },
                      padding: { xs: '2px 6px', sm: '3px 8px' },
                      fontSize: { xs: '0.65rem', sm: '0.7rem' },
                      lineHeight: 1.2,
                      textTransform: 'none',
                      color: isWishlisted ? 'primary.main' : 'pulse.info',
                      backgroundColor: isWishlisted ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '4px',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.12)',
                        color: isWishlisted ? 'primary.main' : 'primary.main',
                      },
                    }}
                  >
                    {isWishlisted ? 'watchlisted' : '+watchlist'}
                  </Button>
                ) : (
                  <Tooltip title={getStatusText()} arrow>
                    <IconButton size="small" onClick={handleClick} sx={{ p: 0 }}>
                      {getStatusIcon(status)}
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          </CardContent>
          <CardStatusMenu
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleClose}
            title={displayTitle}
            statusItems={statusItems}
            status={status}
            onSelectStatus={handleStatusChange}
            // ライブラリ以外（検索など未登録の作品）はステータス以外に項目がほぼ無いので、
            // 横に畳む意味がない。ゲームカードと同じ規則で縦リストにする。
            variant={mode === 'library' ? 'segmented' : 'list'}
            actions={menuActions}
            onDelete={status !== null ? () => handleStatusChange(null) : undefined}
            deleteLabel={t('menu.delete')}
          />
        </Card>
      </Box>
      <ConfirmDialog
        open={openDeleteConfirm}
        title="ステータス削除の確認"
        message="ステータスを削除しますか？"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setOpenDeleteConfirm(false)}
        confirmText="削除"
        cancelText="キャンセル"
      />
      {openReviewDialog && (
        <AnimeReviewDialog
          open={openReviewDialog}
          onClose={() => { setOpenReviewDialog(false); setReviewDialogIsEdit(false); }}
          malId={anime.mal_id}
          // 編集モードは渡さない → ダイアログが自己取得。新規は空で開く
          initialData={reviewDialogIsEdit ? undefined : EMPTY_ANIME_REVIEW}
          animeType={anime.type}
          totalEpisodes={anime.episodes}
          airedFrom={anime.aired_from}
          onReviewSaved={() => setOpenReviewDialog(false)}
        />
      )}
    </>
  );
};

export default AnimeCard;
